import { ApolloClient, Observable, RefetchEventManager } from '@apollo/client';
import { Defer20220824Handler } from '@apollo/client/incremental';
import { ApolloProvider } from '@apollo/client/react';
import { ThemeProvider, StyledEngineProvider } from '@mui/material/styles';
import { useState } from 'react';
import ReactGA from 'react-ga4';
import { RouterProvider } from '@tanstack/react-router';
import type { AnyRouter } from '@tanstack/react-router';

// Direct subpath imports — avoids pulling the entire @amber-ui/core barrel chain
// during app initialization. This is the first file loaded by the browser, so its
// import graph determines how much work the React Compiler must do before first paint.
// Each import targets a single source file rather than traversing the 14-barrel chain.
import { createCache, createLinks } from '@amber-ui/core/src/api/graphql/client';
import { mui_theme } from '@amber-ui/core/src/style/mui-theme';
import { ActiveHubsWithColor } from '@amber-ui/core/src/contexts/activeHubsWithColor';
import { OperationsViewInfoProvider } from '@amber-ui/core/src/contexts/operationsViewInfoContext';
import { AuthProvider, useAuth } from '@amber-ui/core/src/contexts/auth-context';
import { setApolloClient } from '@amber-ui/core/src/router/apollo-module';
import { initRouter } from '@amber-ui/core/src/router/router';
import { ServiceWorkerMessageHandler } from '@amber-ui/core/src/component/util/ServiceWorkerMessageHandler';
// CSS imports cannot be re-exported from a .ts barrel file, so we use a deep path
// into the package source. This resolves via the workspace symlink at node_modules/@amber-ui/core.
import '@amber-ui/core/src/style/main.css';
// Highcharts touch-scroll fix — must load before any chart renders to patch
// Event.prototype.preventDefault for touch events on chart containers.
import '@amber-ui/core/src/util/highcharts-setup';
import '@amber-ui/font/digital-7/digital-7.css';
import '@amber-ui/font/source-sans-pro/source-sans-pro.css';
import '@amber-ui/font/work-sans/work-sans.css';
// The route tree is generated by @tanstack/router-plugin/vite when the dev server
// runs with src/routes/__root.tsx present. It won't exist until task 11.1 creates
// the root route file — TypeScript will report an import error until then.
import { routeTree } from '../routeTree.gen';

// ---------------------------------------------------------------------------
// Module-level singleton initialization — runs once on app start, survives HMR.
// This MUST be at module scope (not inside a component) so that HMR fast-refresh
// of the App component does not re-create the Apollo Client or router.
// ---------------------------------------------------------------------------

function createApolloClient(): ApolloClient {
  const host = window['app_config'].api_host_name || 'http://localhost:3100';

  if (window['app_config'].env === 'prod') {
    ReactGA.initialize('G-8RV8ZMFRKM', {
      gtagOptions: { send_page_view: false },
    });
  }

  return new ApolloClient({
    link: createLinks(host),
    cache: createCache(),
    // @defer incremental delivery handler — processes multipart/mixed responses.
    // NOT actively used (June 2026): no @defer queries are fired yet because Lambda
    // + API Gateway buffers the full response (no streaming benefit). Infrastructure
    // remains configured for future Lambda Function URL migration. Zero cost when idle.
    incrementalHandler: new Defer20220824Handler(),
    refetchEventManager: new RefetchEventManager({
      sources: {
        // Custom source that triggers on both visibilitychange (tab switch within
        // same window) AND window focus (switching between browser windows or apps).
        // Apollo's built-in windowFocusSource only listens for visibilitychange,
        // which doesn't fire when switching between browser windows — only between
        // tabs. Most users have the dashboard in its own window (the debug Chrome
        // instance), so we need both events.
        windowFocus: () => {
          // SSR guard: unreachable in site-public-webapp (browser-only), but kept
          // as defensive code since RefetchEventManager.EventSource is a generic
          // type that could theoretically be evaluated in a test environment.
          if (typeof window === 'undefined') {
            return new Observable(() => {});
          }
          return new Observable((observer) => {
            let lastEmit = 0;
            const DEBOUNCE_MS = 2000; // prevent double-fire from both events

            const maybeEmit = () => {
              const now = Date.now();
              if (now - lastEmit < DEBOUNCE_MS) return;
              lastEmit = now;
              observer.next(new Event('focus'));
            };

            const onVisibilityChange = () => {
              if (document.visibilityState === 'visible') {
                maybeEmit();
              }
            };

            const onFocus = () => {
              maybeEmit();
            };

            document.addEventListener('visibilitychange', onVisibilityChange);
            window.addEventListener('focus', onFocus);

            return () => {
              document.removeEventListener('visibilitychange', onVisibilityChange);
              window.removeEventListener('focus', onFocus);
            };
          });
        },
      },
      handlers: {
        // Custom handler that silently refreshes data on tab focus.
        //
        // Why: nextFetchPolicy demotes all queries to 'cache-first' after their
        // initial fetch. The default handler's reobserve() respects the stored
        // policy and just reads from cache. Users expect fresh data when returning
        // to a tab open for hours/days.
        //
        // Approach: Use client.query({ fetchPolicy: 'network-only' }) to silently
        // fetch fresh data and write it to the InMemoryCache. All useQuery hooks
        // watching that cached data re-render automatically via cache broadcasts —
        // without entering a loading state or showing spinners. This is the true
        // stale-while-revalidate pattern: show current data immediately, update
        // silently in the background.
        windowFocus: ({ client, matchesRefetchOn }) => {
          const queries = client.getObservableQueries('active');

          queries.forEach((observableQuery) => {
            if (!matchesRefetchOn(observableQuery)) return;
            const initialPolicy = observableQuery.options.initialFetchPolicy;
            if (initialPolicy === 'cache-first' || initialPolicy === 'cache-only') return;
            // client.query with network-only fetches from server and writes to cache.
            // useQuery hooks see the cache update via broadcast and re-render with
            // fresh data — no loading state change, no spinners.
            client
              .query({
                query: observableQuery.query,
                variables: observableQuery.variables,
                fetchPolicy: 'network-only',
              })
              .catch(() => {
                // Non-critical: next poll cycle will refresh.
              });
          });
          // Return void — we don't need the handler to track completion.
          // The cache updates propagate to useQuery hooks automatically.
        },
      },
    }),
    defaultOptions: {
      watchQuery: {
        // After the first network fetch, demote aggressive fetch policies to
        // cache-first so that other components mounting the same query read
        // from the cache instead of firing duplicate network requests.
        // Polling queries are unaffected — pollInterval triggers refetch()
        // which always uses network-only regardless of the stored fetchPolicy.
        nextFetchPolicy(currentFetchPolicy, { reason, initialFetchPolicy }) {
          // When variables change, reset to the original policy so the new
          // variable combination still gets a fresh network fetch.
          if (reason === 'variables-changed') {
            return initialFetchPolicy;
          }
          if (currentFetchPolicy === 'network-only' || currentFetchPolicy === 'cache-and-network') {
            return 'cache-first';
          }
          return currentFetchPolicy;
        },
      },
    },
  });
}

// Lazy-initialized singletons. These are created on first access and survive HMR
// updates because module-scope variables are preserved during fast-refresh
// (only the component function re-executes, not the module top-level).
let _apolloClient: ApolloClient | null = null;
let _router: AnyRouter | null = null;

function getAppSingletons(): { apolloClient: ApolloClient; router: AnyRouter } {
  if (!_apolloClient) {
    _apolloClient = createApolloClient();

    // One-time cache migration for the Sqids ID transition (Int → String).
    // Apollo's InMemoryCache keys entities as `__typename:id`. When IDs change
    // from integers ("GrainBin:42") to Sqid strings ("GrainBin:Lqj8a0xT"),
    // stale integer-keyed entries cause cache misses. Clearing forces fresh
    // network fetches that populate the cache with string-keyed entries.
    // Runs once per browser (guarded by localStorage). Safe to remove after
    // all users have loaded this version (i.e., after one full deployment cycle).
    const CACHE_VERSION = 'sqids-v1';
    const CACHE_VERSION_KEY = 'APOLLO_CACHE_VERSION';
    const storedVersion = localStorage.getItem(CACHE_VERSION_KEY);
    if (storedVersion !== CACHE_VERSION) {
      _apolloClient
        .clearStore()
        .then(() => {
          localStorage.setItem(CACHE_VERSION_KEY, CACHE_VERSION);
        })
        .catch(() => {
          // Still set the version to prevent infinite retry loops on cache clear failure.
          // The app can function with stale cache entries — they'll be replaced by fresh
          // network-only fetches as the user navigates.
          localStorage.setItem(CACHE_VERSION_KEY, CACHE_VERSION);
        });
    }

    // Store Apollo Client at module level for route loaders (they run outside
    // the React tree and cannot use hooks). Must be called before initRouter().
    setApolloClient(_apolloClient);
  }

  if (!_router) {
    // Create the data router. This replaces <BrowserRouter> and NavigationSetter:
    // - The router instance is stored at module level for programmatic navigation
    // - router.subscribe('onResolved') closes the nav bar and fires GA pageview tracking
    _router = initRouter(routeTree);
  }

  return { apolloClient: _apolloClient, router: _router };
}

// ---------------------------------------------------------------------------
// App component — function component for proper React Fast Refresh HMR support.
// Class components cannot be fast-refreshed; they cause full remounts that
// destroy context state (including AuthContext), causing the "useAuth must be
// used within an AuthProvider" error on every HMR update.
// ---------------------------------------------------------------------------

function App() {
  // useState with lazy initializer — runs once on first render, survives HMR.
  // The singletons themselves live at module scope and survive HMR module re-evaluation.
  const [{ apolloClient, router }] = useState(getAppSingletons);

  return (
    <ApolloProvider client={apolloClient}>
      <StyledEngineProvider injectFirst>
        <ThemeProvider theme={mui_theme}>
          <AuthProvider apolloClient={apolloClient}>
            <ServiceWorkerMessageHandler apolloClient={apolloClient} />
            <ActiveHubsWithColor>
              <OperationsViewInfoProvider>
                <AppInner router={router} apolloClient={apolloClient} />
              </OperationsViewInfoProvider>
            </ActiveHubsWithColor>
          </AuthProvider>
        </ThemeProvider>
      </StyledEngineProvider>
    </ApolloProvider>
  );
}

/**
 * Inner component that reads auth state from AuthContext and passes it as
 * reactive router context. This ensures route guards (beforeLoad) always see
 * the latest auth state after login/logout without needing a static object
 * at module level.
 */
function AppInner({ router, apolloClient }: { router: AnyRouter; apolloClient: ApolloClient }) {
  const auth = useAuth();
  return <RouterProvider router={router} context={{ apolloClient, auth }} />;
}

export default App;
